import { i18n } from '@/shared/i18n';

export function formatRelativeDate(dateString: string | Date | undefined | null): string {
  if (!dateString) {
    return '';
  }
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);

  // Less than 1 minute
  if (diffInMinutes < 1) {
    return i18n.t('common:utils.dateUtils.now');
  }

  // Less than 1 hour
  if (diffInHours < 1) {
    return i18n.t('common:utils.dateUtils.minutesAgo', { count: diffInMinutes });
  }

  // Less than 24 hours
  if (diffInHours < 24) {
    return i18n.t('common:utils.dateUtils.hoursAgo', { count: diffInHours });
  }

  // Less than 7 days
  if (diffInDays < 7) {
    return i18n.t('common:utils.dateUtils.daysAgo', { count: diffInDays });
  }

  // Less than 4 weeks (approximately a month)
  if (diffInWeeks < 4) {
    return i18n.t('common:utils.dateUtils.weeksAgo', { count: diffInWeeks });
  }

  // Otherwise show "Month DD" format
  return formatDate(date);
}

/**
 * Compact relative time: xm / xh / xd / xw / xmo ago
 * ≤60 min → "xm ago" | ≤24 h → "xh ago" | ≤7 d → "xd ago"
 * ≤4 w  → "xw ago" | otherwise → "xmo ago"
 */
export function formatTimeAgo(dateString: string | Date | undefined | null): string {
  if (!dateString) return '';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffD = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffW = Math.floor(diffD / 7);
  const diffMo = Math.floor(diffD / 30);

  const diffYr = Math.floor(diffD / 365);

  if (diffMin < 1) return i18n.t('common:utils.dateUtils.now2');
  if (diffH < 1) return i18n.t('common:utils.dateUtils.mAgo', { minutes: diffMin });
  if (diffH < 24) return i18n.t('common:utils.dateUtils.hAgo', { hours: diffH });
  if (diffD <= 7) return i18n.t('common:utils.dateUtils.dAgo', { days: diffD });
  if (diffW <= 4) return i18n.t('common:utils.dateUtils.wAgo', { weeks: diffW });
  if (diffMo < 12) return i18n.t('common:utils.dateUtils.moAgo', { months: diffMo });
  return i18n.t('common:utils.dateUtils.yAgo', { years: diffYr });
}

export function formatDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  // Only show year if it's different from current year
  if (dateYear !== currentYear) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString(undefined, options);
}
